function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
import React from "react";
import { rgba } from "polished";
import styled, { css } from "styled-components";
import { lowEmphasisOpacity, pageGutterSpace, zIndex } from "../../../settings";
import breakpoint from "../../../utils/breakpoint";
import getBorderColor from "../../../utils/getBorderColor";
import getBorderRadius from "../../../utils/getBorderRadius";
import getBoxShadow from "../../../utils/getBoxShadow";
import getColor from "../../../utils/getColor";
import getFocusOutline from "../../../utils/getFocusOutline";
import getIconColor from "../../../utils/getIconColor";
import space from "../../../utils/space";
import unit from "../../../utils/unit";
import { cssTextAtm } from "../../atoms/TextAtm/TextAtm.styled";
import { backgroundColor, inputBaseStylesWithHover } from "../InputMol/InputMol.styled";
var ArrowSvg = function ArrowSvg(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("path", {
    d: "m7.406 11.784-5.16-4.526a.675.675 0 0 1 0-1.042.926.926 0 0 1 1.189 0L8 10.22l4.565-4.004a.926.926 0 0 1 1.189 0 .675.675 0 0 1 0 1.042l-5.16 4.526A.9.9 0 0 1 8 12a.901.901 0 0 1-.594-.216Z"
  }));
};
ArrowSvg.defaultProps = {
  width: "16",
  height: "16",
  xmlns: "http://www.w3.org/2000/svg"
};
var selectBackground = backgroundColor;
export var optionsBackground = "backgroundLight";
export var SelectRefContainer = styled.div.withConfig({
  componentId: "sc-1c6sbrv-0"
})(["position:relative;"]);
export var Select = styled.div.withConfig({
  componentId: "sc-1c6sbrv-1"
})(function (_ref) {
  var $hasHover = _ref.$hasHover,
    $hasFocus = _ref.$hasFocus,
    $hasError = _ref.$hasError,
    $size = _ref.$size,
    $onColor = _ref.$onColor,
    $borderRadius = _ref.$borderRadius,
    $isInactive = _ref.$isInactive,
    theme = _ref.theme;
  return css(["", ";display:flex;flex-direction:column;justify-content:center;white-space:nowrap;appearance:none;cursor:pointer;", " ", ""], inputBaseStylesWithHover($hasHover, $hasFocus, $onColor, $size, undefined, true, $borderRadius), $hasError && !$hasFocus && css(["border-color:", ";"], rgba(getColor(theme, "error"), lowEmphasisOpacity)), $isInactive && css(["", " background:", ";cursor:default;"], $hasHover && css(["border:", " solid ", ";"], unit(1), getBorderColor(theme, $onColor, "extraLow")), getColor(theme, "background")));
});
export var Label = styled.p.withConfig({
  componentId: "sc-1c6sbrv-2"
})(function (_ref2) {
  var $hasTextEllipsis = _ref2.$hasTextEllipsis,
    $typography = _ref2.$typography;
  return css(["", ";overflow:hidden;text-overflow:ellipsis;", ""], cssTextAtm({
    typography: $typography,
    onColor: selectBackground,
    emphasis: "medium"
  }), $hasTextEllipsis ? css(["white-space:nowrap;"]) : null);
});
export var Arrow = styled(ArrowSvg).withConfig({
  componentId: "sc-1c6sbrv-3"
})(function (_ref3) {
  var theme = _ref3.theme,
    $isOpen = _ref3.$isOpen,
    $arrowColor = _ref3.$arrowColor;
  return css(["flex-shrink:0;margin-left:", ";transition:transform 0.2s ease 0s;", " ", ""], space(2), $isOpen && css(["transform:rotate(180deg);"]), $arrowColor ? css(["fill:", ";"], getColor(theme, $arrowColor)) : css(["fill:", ";"], getIconColor(theme, selectBackground, "high")));
});
var alignOptionsBreakpointStyles = function alignOptionsBreakpointStyles(align) {
  var alignBreakpoint = align;
  if (_typeof(alignBreakpoint) === "object" && alignBreakpoint.small) {
    return css(["", "{", ":0;}", "{", ":0;}", "{", ":0;}"], breakpoint("small", "medium"), alignBreakpoint.small, breakpoint("medium", "large"), alignBreakpoint.medium, breakpoint("large"), alignBreakpoint.large);
  }
  return css(["", ":0;"], align);
};
export var Options = styled.div.withConfig({
  componentId: "sc-1c6sbrv-4"
})(function (_ref4) {
  var $onColor = _ref4.$onColor,
    $align = _ref4.$align,
    theme = _ref4.theme;
  return css(["position:absolute;z-index:", ";min-width:100%;max-width:calc(100vw - ", ");max-height:", ";margin-top:", ";overflow:auto;overflow-x:hidden;border:", " solid ", ";border-radius:", ";background:", ";box-shadow:", ";", ";"], zIndex.SelectMolOptions, space(pageGutterSpace * 4), space(36), space(0.5), unit(1), getBorderColor(theme, $onColor, "extraLow"), getBorderRadius(theme, "large"), getColor(theme, optionsBackground), getBoxShadow(theme, "small"), alignOptionsBreakpointStyles($align));
});
export var Option = styled.div.withConfig({
  componentId: "sc-1c6sbrv-5"
})(function (_ref5) {
  var $isDisabled = _ref5.$isDisabled,
    $size = _ref5.$size,
    theme = _ref5.theme;
  return css(["position:relative;padding:", ";cursor:", ";overflow-x:auto;", " &:not(:last-child){border-bottom:", " solid ", ";}&:first-child{padding-top:", ";}&:last-child{padding-bottom:", ";}& > div:first-child{white-space:nowrap;}&:focus{", "}", ""], space(2), $isDisabled ? "not-allowed" : "pointer", $size === "small" ? css(["padding:", ";"], "".concat(space(1.5), " ").concat(space(2))) : null, unit(1), getBorderColor(theme, optionsBackground, "extraLow"), space(3), space(3), getFocusOutline(theme, true), !$isDisabled && css(["&:hover{&::before{content:\"\";position:absolute;inset:0;z-index:-1;background:", ";}}"], getColor(theme, "background")));
});
export var Aside = styled.div.withConfig({
  componentId: "sc-1c6sbrv-6"
})(["display:flex;align-items:center;margin-left:", ";> *:not(:last-child){margin-right:", ";}"], space(2), space(1));
export var PromoInfo = styled.div.withConfig({
  componentId: "sc-1c6sbrv-7"
})(["white-space:normal;"]);